"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { AccommodationType, UniqueShip } from "@/interfaces/IUniqueShip";
import {
  Box,
  Collapse,
  Container,
  Dialog,
  List,
  ListItemButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CommonModal from "@/components/common/CommonModal";
import ReactPlayer from "react-player";
import { ReadMoreDrawer } from "./ItineraryHero";
import DeckPlans from "./DeckPlans";

import ExpandLessIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";

import ReadMoreIcon from "@mui/icons-material/AddCircleOutlineRounded";
import ReadLessIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import CruiseDining from "./CruiseDining";
import CruiseOtherFacilities from "./CruiseOtherFacilities";
import { toKebabCase } from "@/utils/helpers";

export type IAccommodationType = {
  [key: string]: AccommodationType[]; // Add this index signature
};

const CruiseFacilities: React.FC<{
  shipData?: UniqueShip;
  selectedTab?: string;
}> = ({ shipData, selectedTab }) => {
  const images = useMemo(() => {
    const accomodation = shipData?.accomodationTypes
      ?.map((x) => ({
        name: x?.images![0]?.name ?? "",
        href: x?.images![0]?.href ?? "",
        type: "image",
      }))
      .concat({ name: "video", href: shipData?.accommodation?.video ?? "", type: "video" })
      .sort((a, b) => {
        if (a?.type === "video" && b?.type !== "video") {
          return -1;
        }
        if (b?.type === "video" && a?.type !== "video") {
          return 1;
        }
        return 0;
      });

    const dining = shipData?.diningOptions
      ?.map((x) => ({
        name: x?.images![0]?.name ?? "",
        href: x?.images![0]?.href ?? "",
        type: "image",
      }))
      .concat({ name: "video", href: shipData?.dining?.video ?? "", type: "video" })
      .sort((a, b) => {
        if (a?.type === "video" && b?.type !== "video") {
          return -1;
        }
        if (b?.type === "video" && a?.type !== "video") {
          return 1;
        }
        return 0;
      });

    const enrichment = shipData?.enrichmentTypes
      ?.map((x) => ({
        name: x?.images![0]?.name ?? "",
        href: x?.images![0]?.href ?? "",
        type: "image",
      }))
      .concat({ name: "video", href: shipData?.enrichment?.video ?? "", type: "video" })
      .sort((a, b) => {
        if (a?.type === "video" && b?.type !== "video") {
          return -1;
        }
        if (b?.type === "video" && a?.type !== "video") {
          return 1;
        }
        return 0;
      });

    const entertainment = shipData?.entertainmentTypes
      ?.map((x) => ({
        name: x?.images![0]?.name ?? "",
        href: x?.images![0]?.href ?? "",
        type: "image",
      }))
      .concat({ name: "video", href: shipData?.entertainment?.video ?? "", type: "video" })
      .sort((a, b) => {
        if (a?.type === "video" && b?.type !== "video") {
          return -1;
        }
        if (b?.type === "video" && a?.type !== "video") {
          return 1;
        }
        return 0;
      });

    const healthFitness = shipData?.healthFitnessTypes
      ?.map((x) => ({
        name: x?.images![0]?.name ?? "",
        href: x?.images![0]?.href ?? "",
        type: "image",
      }))
      .concat({ name: "video", href: shipData?.healthAndFitness?.video ?? "", type: "video" })
      .sort((a, b) => {
        if (a?.type === "video" && b?.type !== "video") {
          return -1;
        }
        if (b?.type === "video" && a?.type !== "video") {
          return 1;
        }
        return 0;
      });

    const kidsAndTeens = shipData?.kidTeenTypes
      ?.map((x) => ({
        name: x?.images![0]?.name ?? "",
        href: x?.images![0]?.href ?? "",
        type: "image",
      }))
      .concat({ name: "video", href: shipData?.kidsAndTeens?.video ?? "", type: "video" })
      .sort((a, b) => {
        if (a?.type === "video" && b?.type !== "video") {
          return -1;
        }
        if (b?.type === "video" && a?.type !== "video") {
          return 1;
        }
        return 0;
      });

    return {
      accomodation: accomodation,
      dining: dining,
      enrichment: enrichment,
      entertainment: entertainment,
      healthFitness: healthFitness,
      kidsAndTeens: kidsAndTeens,
    };
  }, [shipData]);

  const uniqueShip = [
    {
      intro: shipData?.dining,
      name: "Dining",
      subTitle: "Savor a variety of delicious cuisines, from fine dining to casual buffets.",
      images: images.dining,
      facility: [],
    },
    {
      intro: shipData?.entertainment,
      name: "Entertainment",
      subTitle:
        "A dynamic mix of shows, live performances, and activities to suit all tastes, ensuring non-stop fun.",
      images: images.entertainment,
      facility: shipData?.entertainmentTypes,
    },
    {
      intro: shipData?.kidsAndTeens,
      name: "Young Travellers",
      subTitle:
        "Dedicated spaces and exciting programs designed to keep kids and teens entertained, active, and engaged.",
      images: images.kidsAndTeens,
      facility: shipData?.kidTeenTypes,
    },
    {
      intro: shipData?.enrichment,
      name: "Enrichment",
      subTitle:
        "Inspiring activities like art classes, cultural tours, and guest lectures to broaden your horizons.",
      images: images.enrichment,
      facility: shipData?.enrichmentTypes,
    },
    {
      intro: shipData?.healthAndFitness,
      name: "Health and Fitness",
      subTitle:
        "Facilities and programs focused on well-being, including gyms, fitness classes, and rejuvenating spa treatments.",
      images: images.healthFitness,
      facility: shipData?.healthFitnessTypes,
    },
    {
      intro: null,
      name: "Deck Plans",
      subTitle:
        "A visual guide showing the layout of the ship’s decks, including cabins, amenities, and key facilities.",
      images: null,
      facility: [],
    },
  ];

  const [isReadMore, setIsReadMore] = useState(false);
  const [sectionID, setSectionID] = useState("");

  const selectedReadMoreButton =
    sectionID == "overview" ||
    sectionID == "dining" ||
    sectionID == "entertainment" ||
    sectionID == "young-travellers" ||
    sectionID == "enrichment" ||
    sectionID == "health-and-fitness";

  const lessThanMD = useMediaQuery("(max-width: 900px)");
  const [openVideo, setOpenVideo] = useState(false);

  return (
    <Container>
      <Grid container size={{ xs: 12 }}>
        {uniqueShip
          .filter((x) => toKebabCase(x.name) === selectedTab)
          .map((data, dataIndex) => (
            <Grid key={dataIndex} size={{ xs: 12, md: 12 }} id={`${toKebabCase(data?.name) ?? ""}`}>
              <Box sx={{}}>
                {data?.intro?.video && (
                  <Box
                    sx={{
                      width: { xs: "calc(100% + 2rem)", sm: "100%", md: "440px" },
                      height: { xs: "100%", md: "250px" },
                      m: {
                        xs: " 0rem 0rem 0rem -1rem",
                        sm: " 0rem 0rem 1rem 0rem",
                        md: " 0rem 0rem 0rem 1.2rem",
                      },
                      position: "relative",
                      float: { md: "right" },
                      zIndex: 10,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: { xs: "100%", sm: "100%" },
                        height: { xs: "200px", sm: "250px" },
                        m: { xs: "0px 0px 16px 0px", sm: "0px 16px 0px 0px" },
                        float: { md: "left" },
                        iframe: {
                          boxShadow:
                            "rgb(27 127 204 / 26%) 0px 10px 36px 0px, rgba(255, 255, 255, .2) 0px 0px 0px 1px",
                          borderRadius: { sm: "15px" },
                        },
                      }}
                    >
                      <ReactPlayer
                        url={data?.intro?.video ?? ""}
                        width="100%"
                        height="100%"
                        config={{
                          youtube: {
                            playerVars: {
                              modestbranding: 1,
                              autoPlay: 1,
                              rel: 0,
                              fs: 0,
                            },
                          },
                        }}
                        controls={true}
                        playIcon={<></>}
                        muted
                        loop={true}
                        volume={0}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        borderRadius: { sm: "15px" },
                        bgcolor: "#0000",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setOpenVideo(true);
                      }}
                    ></Box>
                  </Box>
                )}

                <Box>
                  <Box>
                    <Typography
                      variant="h2"
                      gutterBottom
                      sx={{
                        fontSize: { xs: "2rem", md: "3rem" },
                        mt: { xs: "1rem", md: "1rem" },
                        color: "#060273",
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      {data.name}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{
                        mt: { xs: "-0.7rem", md: "-1.2rem" },
                        mb: { xs: "1.5rem", md: "2rem" },
                        color: "#060273",
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      {data.subTitle}
                    </Typography>
                  </Box>

                  <Contents
                    data={data?.intro?.intro ?? ""}
                    isReadMore={isReadMore && selectedReadMoreButton}
                    onClick={() => {
                      setIsReadMore((prev) => !prev);
                      setSectionID(toKebabCase(data?.name) ?? "");
                    }}
                    paragraphID={toKebabCase(data?.name ?? "") ?? ""}
                  />
                </Box>
              </Box>

              <Gallery open={openVideo} setModalOpen={setOpenVideo}>
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: "100%", sm: "100%" },
                    height: { xs: "200px", sm: "500px" },
                    m: { xs: "0px 0px 16px 0px", sm: "0px 16px 0px 0px" },
                    float: { xs: "top", sm: "left" },
                    iframe: {
                      boxShadow:
                        "rgb(27 127 204 / 26%) 0px 10px 36px 0px, rgba(255, 255, 255, .2) 0px 0px 0px 1px",
                      borderRadius: { sm: "15px" },
                    },
                  }}
                >
                  <ReactPlayer
                    url={data?.intro?.video ?? ""}
                    width="100%"
                    height="100%"
                    config={{
                      youtube: {
                        playerVars: {
                          modestbranding: 1,
                          autoPlay: 1,
                          rel: 0,
                          fs: 0,
                        },
                      },
                    }}
                    controls={true}
                    playIcon={<></>}
                    muted
                    loop={true}
                    volume={0}
                  />
                </Box>
              </Gallery>

              {data.name == "Dining" && <CruiseDining shipData={shipData} />}

              {data.name == "Deck Plans" && (
                <DeckPlans data={shipData?.deckplans} name={data?.name} shipData={shipData} />
              )}

              {!["Dining", "Accommodation", "Deck Plans"].includes(data?.name) &&
                (data.facility?.length ?? 0) > 0 && (
                  <CruiseOtherFacilities
                    data={data?.facility}
                    name={data?.name}
                    selectedTab={selectedTab}
                  />
                )}
            </Grid>
          ))}
      </Grid>

      {isReadMore && lessThanMD && selectedReadMoreButton && (
        <ReadMoreDrawer
          isReadMore={isReadMore}
          onClose={() => {
            setIsReadMore(false);
            setSectionID("");
          }}
          title={
            sectionID == "overview"
              ? "Overview"
              : sectionID == "dining"
              ? "Dining"
              : sectionID == "entertainment"
              ? "Entertainment"
              : sectionID == "young-travellers"
              ? "Kids and Teens"
              : sectionID == "enrichment"
              ? "Enrichment"
              : sectionID == "health-and-fitness"
              ? "Health and Fitness"
              : ""
          }
        >
          <Box
            sx={{
              "ul li": {
                mb: "7px",
                listStyle: "none",
                lineHeight: 1.4,
                fontSize: "1rem",
                "&:before": {
                  content: "'⚓︎'",
                  position: "relative",
                  top: "-2px",
                  color: "#167ac6",
                  fontSize: "12px",
                  pr: "0.5rem",
                  ml: "0rem",
                },
              },
              "div > p": {
                "&:not(p:first-of-type)": {
                  pt: "1rem",
                },
                b: {
                  fontSize: "1.3rem",
                  lineHeight: 1,
                },
              },
            }}
          >
            <Box
              sx={{ width: "100%", lineHeight: "30px" }}
              dangerouslySetInnerHTML={{
                __html:
                  sectionID == "overview"
                    ? shipData?.introduction ?? ""
                    : sectionID == "dining"
                    ? shipData?.dining?.intro ?? ""
                    : sectionID == "entertainment"
                    ? shipData?.enrichment?.intro ?? ""
                    : sectionID == "young-travellers"
                    ? shipData?.kidsAndTeens?.intro ?? ""
                    : sectionID == "enrichment"
                    ? shipData?.enrichment?.intro ?? ""
                    : sectionID == "health-and-fitness"
                    ? shipData?.healthAndFitness?.intro ?? ""
                    : "",
              }}
            ></Box>
          </Box>
        </ReadMoreDrawer>
      )}

      {isReadMore && !lessThanMD && selectedReadMoreButton && (
        <CommonModal
          open={isReadMore}
          onClose={() => {
            setIsReadMore(false);
            setSectionID("");
          }}
          modalTitle={
            sectionID == "overview"
              ? "Overview"
              : sectionID == "dining"
              ? "Dining"
              : sectionID == "entertainment"
              ? "Entertainment"
              : sectionID == "young-travellers"
              ? "Young Travellers"
              : sectionID == "enrichment"
              ? "Enrichment"
              : sectionID == "health-and-fitness"
              ? "Health and Fitness"
              : ""
          }
        >
          <Box
            sx={{
              "ul li": {
                mb: "7px",
                listStyle: "none",
                lineHeight: 1.4,
                fontSize: "1rem",
                "&:before": {
                  content: "'⚓︎'",
                  position: "relative",
                  top: "-2px",
                  color: "#167ac6",
                  fontSize: "12px",
                  pr: "0.5rem",
                  ml: "0rem",
                },
              },
              "div > p": {
                "&:not(p:first-of-type)": {
                  pt: "1rem",
                },
                b: {
                  fontSize: "1.3rem",
                  lineHeight: 1,
                },
              },
            }}
          >
            <Box
              sx={{ width: "100%", lineHeight: "30px" }}
              dangerouslySetInnerHTML={{
                __html:
                  sectionID == "overview"
                    ? shipData?.introduction ?? ""
                    : sectionID == "dining"
                    ? shipData?.dining?.intro ?? ""
                    : sectionID == "entertainment"
                    ? shipData?.enrichment?.intro ?? ""
                    : sectionID == "young-travellers"
                    ? shipData?.kidsAndTeens?.intro ?? ""
                    : sectionID == "enrichment"
                    ? shipData?.enrichment?.intro ?? ""
                    : sectionID == "health-and-fitness"
                    ? shipData?.healthAndFitness?.intro ?? ""
                    : "",
              }}
            ></Box>
          </Box>
        </CommonModal>
      )}
    </Container>
  );
};
export default CruiseFacilities;

export const ReadMoreButton: React.FC<{
  isReadMore?: boolean;
  onClick?: () => void;
  isV2?: boolean;
  isItineraryMobile?: boolean;
}> = ({ isReadMore, onClick, isV2, isItineraryMobile }) => {
  return (
    <Box
      sx={{
        width: "fit-content",
        p: "2px 3px 2px 8px",
        fontSize: { xs: "14px", md: "14px" },
        mt: "8px",
        bgcolor: "#edf1ff",
        color: isItineraryMobile ? "#FF84B1" : "#060273",
        fontWeight: 500,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "3px",
        border: "1px solid",
        borderColor: "#06027340",
        borderRadius: "15px",
        lineHeight: 1.2,
        svg: {
          width: "21px",
          height: "21px",
        },
        "&:hover": {
          borderColor: "#06027350",
          color: "#fff",
          bgcolor: "#060273",
        },
        ...(!isV2 && {
          p: "0px",
          bgcolor: "unset",
          border: "none",
          borderRadius: "0px",
          "&:hover": {
            borderColor: "unset",
            color: "#E30053",
            bgcolor: "unset",
          },
        }),
      }}
      onClick={onClick}
    >
      <Box sx={{ mt: "-1px" }}>{!isReadMore ? "Read More" : "Read Less"}</Box>
      {!isReadMore ? <ReadMoreIcon /> : <ReadLessIcon />}
    </Box>
  );
};

export const Gallery: React.FC<{
  open: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  children?: React.ReactNode;
}> = ({ open, setModalOpen, children }) => {
  const lessThanMD = useMediaQuery("(max-width: 900px)");

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setModalOpen(false)}
        fullWidth={true}
        maxWidth="md"
        disableAutoFocus
        disableRestoreFocus
        disableEnforceFocus
        fullScreen={lessThanMD}
        sx={{
          ".MuiPaper-root": {
            backdropFilter: "unset",
            overflow: "hidden",
            bgcolor: "unset",
          },
          ".MuiPaper-root.MuiDialog-paper": {
            boxShadow: { xs: "none", md: "none" },
            border: { xs: "none", md: "none" },
            borderRadius: { xs: "0px", md: "15px" },
            width: "100%",
          },
          ".MuiBackdrop-root.MuiModal-backdrop": {
            backdropFilter: "blur(5px)",
          },
        }}
      >
        <Box sx={{ p: { xs: "0px", md: "1rem" }, position: "relative" }}>
          {children}
          <Box
            sx={{
              position: "absolute",
              top: "0rem",
              right: "0rem",
              zIndex: 1,
              padding: "3px",
              borderRadius: "15px",
              bgcolor: "#ccc",
              color: "#222",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              svg: {
                fontSize: "24px",
              },
            }}
            onClick={() => {
              setModalOpen((prev) => !prev);
            }}
          >
            <CloseIcon />
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export const Contents: React.FC<{
  data?: string;
  isReadMore: boolean;
  isTypesOfFacilities?: boolean;
  onClick: () => void;
  sectionID?: string;
  setSectionID?: React.Dispatch<React.SetStateAction<string>>;
  setIsReadMore?: React.Dispatch<React.SetStateAction<boolean>>;
  paragraphID?: string;
  isCruiseLine?: boolean;
  isItineraryMobile?: boolean;
  drawerTitle?: string;
}> = ({
  data,
  isReadMore,
  isTypesOfFacilities,
  onClick,
  sectionID,
  setSectionID,
  setIsReadMore,
  paragraphID,
  isCruiseLine,
  isItineraryMobile,
  drawerTitle,
}) => {
  const paragraphRef = useRef<HTMLElement | null>(null);
  const lessThanMD = useMediaQuery("(max-width: 900px)");

  const getVisibleLineCount = (element: HTMLElement | null): number => {
    if (!element) return 0;

    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
    const totalHeight = element.clientHeight;
    return Math.round(totalHeight / lineHeight);
  };

  const [lineCount, setLineCount] = useState(0);

  const readMoreLineCount = lessThanMD
    ? 7
    : isTypesOfFacilities
    ? 14
    : isCruiseLine
    ? 10
    : isItineraryMobile
    ? 3
    : 5;

  useEffect(() => {
    if (paragraphRef && paragraphID) {
      setLineCount(getVisibleLineCount(paragraphRef.current));
    }
  }, [paragraphRef, paragraphID]);

  const selectedReadMoreButton =
    sectionID == "accommodation" ||
    sectionID == "dining" ||
    sectionID == "entertainment" ||
    sectionID == "young-travellers" ||
    sectionID == "enrichment" ||
    sectionID == "health-and-fitness" ||
    sectionID == "members-club" ||
    sectionID == "reasons-to-book" ||
    sectionID == "introduction";

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        id={"content"}
        sx={{
          position: "relative",
          overflow: { xs: "hidden" },
          WebkitLineClamp: { xs: 7, md: 5 },
          ...(isTypesOfFacilities && {
            WebkitLineClamp: { xs: 7, md: 14 },
          }),
          ...(isCruiseLine && {
            WebkitLineClamp: { xs: 7, md: 10 },
          }),
          ...(isItineraryMobile && {
            WebkitLineClamp: { xs: 7, md: 10 },
          }),
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          textOverflow: "ellipsis",
          color: isItineraryMobile && !isReadMore ? "#fff" : "#555",
          fontWeight: 400,
          fontSize: { xs: "14px", md: "15px" },
          ul: {
            pl: "25px",
            mt: "5px",
            li: {
              mb: "7px",
              listStyle: "none",
              "&::before": {
                content: "'✦'",
                color: isItineraryMobile && !isReadMore ? "#C6A7FF" : "#060273",
                fontSize: "16px",
                paddingRight: "10px",
                marginLeft: "-23px",
              },
            },
          },
          "div > p": {
            m: "10px 0px 6px",
            "&:first-of-type": {
              mt: { xs: "0px", md: "-4px" },
            },
            "&:not(p:first-of-type)": {
              pt: { xs: "1rem", md: "0px" },
            },
            color: isItineraryMobile && !isReadMore ? "#fff" : "#555",
            fontWeight: 400,
            fontSize: { xs: "14px", md: "15px" },
            b: {
              fontSize: "1.1rem",
              fontWeight: 600,
            },
          },
        }}
      >
        <Box
          ref={paragraphRef}
          sx={{
            width: "100%",
            lineHeight: 1.45,
          }}
          dangerouslySetInnerHTML={{
            __html: data ?? "",
          }}
        ></Box>

        {isReadMore && lessThanMD && (selectedReadMoreButton || isItineraryMobile) && (
          <ReadMoreDrawer
            isReadMore={isReadMore}
            onClose={() => {
              if (setSectionID) {
                setSectionID("");
              }
              if (setIsReadMore) {
                setIsReadMore(false);
              }
            }}
            title={
              sectionID == "accommodation"
                ? "Accommodation"
                : sectionID == "dining"
                ? "Dining"
                : sectionID == "entertainment"
                ? "Entertainment"
                : sectionID == "young-travellers"
                ? "Kids and Teens"
                : sectionID == "enrichment"
                ? "Enrichment"
                : sectionID == "health-and-fitness"
                ? "Health and Fitness"
                : sectionID == "members-club"
                ? "Members Club"
                : sectionID == "reasons-to-book"
                ? "Reasons to Book"
                : sectionID == "introduction"
                ? "Introduction"
                : isItineraryMobile
                ? drawerTitle ?? ""
                : ""
            }
          >
            <Box
              sx={{
                color: isItineraryMobile && !isReadMore ? "#fff" : "#555",
                fontWeight: 400,
                fontSize: { xs: "14px", md: "15px" },
                ul: {
                  pl: "25px",
                  mt: "5px",
                  li: {
                    mb: "7px",
                    listStyle: "none",
                    "&::before": {
                      content: "'✦'",
                      color: isItineraryMobile && !isReadMore ? "#C6A7FF" : "#060273",
                      fontSize: "16px",
                      paddingRight: "10px",
                      marginLeft: "-23px",
                    },
                  },
                },
                "div > p": {
                  mb: "8px",
                  "&:first-of-type": {
                    mt: { xs: "0px", md: "-4px" },
                  },
                  "&:not(p:first-of-type)": {
                    pt: { xs: "1rem", md: "0px" },
                  },
                  color: isItineraryMobile && !isReadMore ? "#fff" : "#555",
                  fontWeight: 400,
                  fontSize: { xs: "14px", md: "15px" },
                  b: {
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  },
                },
              }}
            >
              <Box
                sx={{ width: "100%", lineHeight: "30px" }}
                dangerouslySetInnerHTML={{
                  __html: data ?? "",
                }}
              ></Box>
            </Box>
          </ReadMoreDrawer>
        )}

        {isReadMore && !lessThanMD && selectedReadMoreButton && (
          <CommonModal
            open={isReadMore}
            onClose={() => {
              if (setSectionID) {
                setSectionID("");
              }
              if (setIsReadMore) {
                setIsReadMore(false);
              }
            }}
            modalTitle={
              sectionID == "accommodation"
                ? "Accommodation"
                : sectionID == "dining"
                ? "Dining"
                : sectionID == "entertainment"
                ? "Entertainment"
                : sectionID == "young-travellers"
                ? "Kids and Teens"
                : sectionID == "enrichment"
                ? "Enrichment"
                : sectionID == "health-and-fitness"
                ? "Health and Fitness"
                : sectionID == "members-club"
                ? "Members Club"
                : sectionID == "reasons-to-book"
                ? "Reasons to Book"
                : sectionID == "introduction"
                ? "Introduction"
                : ""
            }
          >
            <Box
              sx={{
                color: isItineraryMobile ? "#fff" : "#555",
                fontWeight: 400,
                fontSize: { xs: "14px", md: "15px" },
                ul: {
                  pl: "25px",
                  mt: "5px",
                  li: {
                    mb: "7px",
                    listStyle: "none",
                    "&::before": {
                      content: "'✦'",
                      color: isItineraryMobile ? "#C6A7FF" : "#060273",
                      fontSize: "16px",
                      paddingRight: "10px",
                      marginLeft: "-23px",
                    },
                  },
                },
                "div > p": {
                  mb: "8px",
                  "&:first-of-type": {
                    mt: { xs: "0px", md: "-4px" },
                  },
                  "&:not(p:first-of-type)": {
                    pt: { xs: "1rem", md: "0px" },
                  },
                  color: isItineraryMobile ? "#fff" : "#555",
                  fontWeight: 400,
                  fontSize: { xs: "14px", md: "15px" },
                  b: {
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  },
                },
              }}
            >
              <Box
                sx={{ width: "100%" }}
                dangerouslySetInnerHTML={{
                  __html: data ?? "",
                }}
              ></Box>
            </Box>
          </CommonModal>
        )}
      </Box>

      {lineCount >= readMoreLineCount && (
        <ReadMoreButton
          isReadMore={isReadMore}
          onClick={onClick}
          isItineraryMobile={isItineraryMobile}
        />
      )}
    </Box>
  );
};

export const CustomNestedButtons: React.FC<{
  label: string;
  isDefaultOpen: boolean;
  children: React.ReactNode;
  isV2Design?: boolean;
}> = ({ label, children, isDefaultOpen, isV2Design }) => {
  const [listOpen, setListOpen] = useState(isDefaultOpen);
  return (
    <List
      sx={{ width: "100%", maxWidth: 360 }}
      component="nav"
      aria-labelledby="nested-list-subheader"
      id={`cabin_${label.toLocaleLowerCase()}`}
    >
      <ListItemButton
        onClick={() => setListOpen((prev) => !prev)}
        sx={{
          color: "#fff",
          fontFamily: "'blauerNue', 'blauerNue Fallback'",
          fontWeight: 700,
          fontSize: { xs: "16px", md: "26px" },
          display: "flex",
          justifyContent: "space-between",
          p: "5px 0px 5px 0px",
          mb: "8px",
          borderBottom: listOpen ? "2px solid #06027300" : "2px solid #fff7",
          // borderBottom: "2px solid #06027300",
          transition: "all 0.3s",
          "&:hover": {
            // borderBottom: "2px solid #060273",
            borderBottom: listOpen ? "2px solid #060273" : "2px solid #fffa",
            bgcolor: "primary.main",
            color: "#FFDEEA",
            svg: {
              bgcolor: "primary.main",
              color: "#FFDEEA",
            },
          },
          ...(isV2Design && {
            color: "primary.main",
            svg: {
              color: "primary.main",
            },
            "&:hover": {
              bgcolor: "unset",
              color: "#E30053",
              svg: {
                bgcolor: "unset",
                color: "#E30053",
              },
            },
          }),
        }}
      >
        <Box component="span">{label}</Box>
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            borderRadius: "16px",
            svg: {
              color: "#fff",
              width: "30px",
              height: "30px",
              borderRadius: "16px",
            },
          }}
        >
          {listOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </ListItemButton>
      <Collapse in={listOpen} timeout="auto" unmountOnExit>
        <Box sx={{ pl: "0px" }}>{children}</Box>
      </Collapse>
    </List>
  );
};
